/* eslint-disable array-callback-return */
import {Box, Button, Checkbox, Input, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import {useB2CContext} from 'src/layouts/b2c-layout/contexts/B2CContext'
import {TitleAlt} from 'src/pages/self-register/select-plan/components/PlanSelectBase'
import api from '../../../services/api/api'
import {CameraHasObjects, CreateActions, ObjectIncludesType} from '../../../services/api/endpoints/ObjectEndpoint'
import handleErrorWithSnackbar from '../../../utilities/handleErrorWithSnackbar'
import {objectTypeList} from '../../alarms/AlarmsFunctions'
import {WrapperContentArea} from '../camera-rtmp-area/CameraRtmpAreaBase'
import {ContainerPageSelfRegister, WrapperContent} from '../camera-rtmp-configuration/CameraRTMPConfigurationBase'
import Stepper from '../components/Stepper/Stepper'
import YoutubeEmbed from '../components/youtube-embed/YoutubeEmbed'
import {GridOfCheckbox} from './CameraRtmpActionsBase'

type CameraRtmpActionsProps = {
  areaIdCreated: number[]
  cameraConfigId: number
  cameraId: number
}

type DetectionOptions = 'person' | 'car' | 'motorcycle' | 'animals' | 'crowd' | 'cars crowd' | 'bicycle' | 'bird' | 'cat' | 'dog' | 'horse' | 'sheep' | 'cow'

const DetectionsMaped: DetectionOptions[] = ['person', 'car', 'motorcycle', 'animals', 'crowd', 'cars crowd']

const LabelDetectionsDictionary = {
  person: 'Pessoa',
  car: 'Carro',
  motorcycle: 'Moto / Bicicleta',
  crowd: 'Aglomeração de Pessoas',
  animals: 'Animais',
  'cars crowd': 'Aglomeração de Carros',
  bicycle: 'bicycle',
  bird: 'bird',
  cat: 'cat',
  dog: 'dog',
  horse: 'horse',
  sheep: 'sheep',
  cow: 'cow',
}

type Detections = {
  [key in DetectionOptions]: boolean
}

function CameraRtmpActions(props: CameraRtmpActionsProps) {
  const {areaIdCreated, cameraConfigId, cameraId} = props
  const {enqueueSnackbar} = useSnackbar()
  const {cameraCreatedPrevious} = useB2CContext()

  const [detections, setDetections] = useState<Detections>({
    person: false,
    car: false,
    motorcycle: false,
    crowd: false,
    animals: false,
    'cars crowd': false,
    bicycle: false,
    bird: false,
    cat: false,
    dog: false,
    cow: false,
    horse: false,
    sheep: false,
  })

  useEffect(() => {
    if (detections.animals !== detections.cat) {
      setDetections((prev) => {
        return {
          ...prev,
          bird: detections.animals,
          cat: detections.animals,
          dog: detections.animals,
          cow: detections.animals,
          horse: detections.animals,
          sheep: detections.animals,
        }
      })
    }
    if (detections.motorcycle !== detections.bicycle) {
      setDetections((prev) => {
        return {
          ...prev,
          bicycle: detections.motorcycle,
        }
      })
    }
  }, [detections])

  const [telegram, setTelegram] = useState(false)
  const [telegramChatId, setTelegramChatId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [cameraName, setCameraName] = useState(cameraCreatedPrevious?.name ?? '')
  const [registeredObjects, setRegisteredObjects] = useState<CameraHasObjects[]>([])
  const blockDeleteRequests = useRef(false)

  useEffect(() => {
    async function getObjects() {
      try {
        if (blockDeleteRequests.current) return
        blockDeleteRequests.current = true
        const includes: ObjectIncludesType[] = ['interestArea', 'objectType', 'actions']
        const response = await api.object.getAllObjectsOnCamera({cameraId, includes})
        const objects = response.data.data
        const objectNames = objects.map((it) => it.objectType.name)
        const chatId = objects.find((it) => it.actions.filter((it) => it.telegramChatId).length)?.actions.find((it) => it.telegramChatId)?.telegramChatId
        const objectHasCarsCrowd = objects.find((it) => it.objectType.name === 'car')
        if (chatId) {
          setTelegram(true)
          setTelegramChatId(`${chatId}`)
        }
        setRegisteredObjects(objects)
        setDetections((prev) => {
          return {
            ...prev,
            person: objectNames.includes('person'),
            car: objectNames.includes('car'),
            motorcycle: objectNames.includes('motorcycle'),
            bicycle: objectNames.includes('motorcycle'),
            animals: objectNames.includes('cat') || objectNames.includes('dog'),
            crowd: objectNames.includes('crowd'),
            'cars crowd': objectHasCarsCrowd ? objectHasCarsCrowd?.threshold > 1 : false,
          }
        })
      } catch (error) {
        return
      } finally {
        blockDeleteRequests.current = false
      }
    }
    getObjects()
  }, [cameraConfigId])

  const handleCreateObjectAndAction = async () => {
    try {
      setIsLoading(true)

      if (cameraName) await api.camera.updateCamera(cameraId, {name: cameraName})

      const options: DetectionOptions[] = ['person', 'car', 'motorcycle', 'crowd', 'cars crowd', 'bicycle', 'bird', 'cat', 'dog', 'horse', 'sheep', 'cow']

      for await (let possibleOption of options) {
        const selectedThreshold = possibleOption === 'cars crowd' || possibleOption === 'crowd' ? 2 : 1
        const userWants = detections[possibleOption]

        if (possibleOption === 'cars crowd') possibleOption = 'car'
        const isRegistered = registeredObjects.find((it) => it.objectType.name === possibleOption && selectedThreshold === it.threshold)
        const shouldSave = !isRegistered && userWants
        const shouldRemove = isRegistered && !userWants

        const objectType = objectTypeList.find((it) => it.name === possibleOption)
        if (!objectType) throw Error(`Object ${possibleOption} should be found`)
        const objectTypeId = objectType.id

        if (shouldSave) {
          const objectCreated = await api.object.createAllTypesObjects({
            confidence: 0.4,
            isActive: true,
            threshold: selectedThreshold,
            interestAreaId: areaIdCreated[0],
            objectTypeId: objectTypeId,
            type: 'interest-area',
          })

          if (telegram) {
            const telegramAction: CreateActions = {type: 'telegram', outputActionId: +telegramChatId}
            await api.object.putActions(objectCreated.data.data.id, [telegramAction])
          }
        }
        if (shouldRemove) {
          await api.object.deleteAllTypesObject(isRegistered.id)
        }

        const actionWithTelegram = isRegistered?.actions.find((action) => action.telegramChatId)
        const shouldRemoveActionWithTelegram = actionWithTelegram && !telegram
        const shouldCreateActionWithTelegram = isRegistered && !actionWithTelegram && telegram

        if (shouldRemoveActionWithTelegram) await api.object.deleteAction(actionWithTelegram.id)
        if (shouldCreateActionWithTelegram) await api.object.putActions(isRegistered.id, [{type: 'telegram', outputActionId: +telegramChatId}])
      }

      window.location.href = '/status?type=success'
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Erro ao finalizar configuração')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisabledButton = () => {
    if (detections.car || detections.motorcycle || detections.person || detections.animals || detections['cars crowd'] || detections.crowd) return false
    return true
  }

  return (
    <ContainerPageSelfRegister>
      <Box display='flex' direction='column' align='center' justifyContent='center'>
        <Stepper activeStep={2} stepTitles={['Dados da câmera', 'Personalizar área', 'Finalizar']} />

        <WrapperContentArea>
          <TitleAlt>
            Chegamos a última etapa, agora pra finalizar <br /> preencha as informações sobre a detecção configurada
          </TitleAlt>
          <WrapperContent>
            {telegram && <YoutubeEmbed embedId='SVfiwRCNST4' />}
            <Box display='flex' direction='column' align='flex-start' justifyContent='space-between' rowGap='25px' height='100%' minHeight='250px'>
              <Input.Root
                labelFontWeight='400'
                placeholder={cameraCreatedPrevious?.name ?? ''}
                label='Descrição da câmera'
                value={cameraName}
                controlled
                onChange={(e) => setCameraName(e.target.value)}
              />

              <Box display='flex' direction='column' align='flex-start' justifyContent='flex-start' rowGap='0px'>
                <Typography variant='span' size='14px' color='#344054'>
                  Selecione abaixo o que você deseja detectar
                </Typography>

                <GridOfCheckbox>
                  {DetectionsMaped.map((it) => {
                    return (
                      <Checkbox
                        labelSize='14px'
                        color='#344054'
                        label={LabelDetectionsDictionary[it]}
                        padding='0 12px 0 0'
                        checked={detections[it]}
                        onChange={() =>
                          setDetections((prev) => {
                            return {
                              ...prev,
                              [it]: !detections[it],
                            }
                          })
                        }
                      />
                    )
                  })}
                </GridOfCheckbox>
              </Box>

              <Box display='flex' direction='column' align='flex-start' justifyContent='flex-start' minWidth='310px' rowGap='0px' width='100%'>
                <Typography variant='span' size='14px' color='#344054'>
                  Selecione abaixo qual a forma de notificação
                </Typography>

                <Box display='flex' columnGap='30px'>
                  <Checkbox labelSize='14px' label='Portal Brain' checked={true} />
                  <Checkbox labelSize='14px' label='App Brain' checked={true} />
                  <Checkbox labelSize='14px' label='Telegram' checked={telegram} onChange={() => setTelegram((prev) => !prev)} />
                </Box>
                {telegram && (
                  <Box marginTop='12px' width='100%'>
                    <Input.Root
                      labelFontWeight='400'
                      controlled
                      width='100%'
                      label={'Informe o código do telegram'}
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                      mask={(value) => {
                        if (value.length <= 19 && value.match(/^-?([0-9]+)?$/)) return value
                        return telegramChatId
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box display='flex' align='center' justifyContent='center' width='100%'>
                <Button
                  variant='contained'
                  themes='primary'
                  fontSize='14px'
                  width='150px'
                  isLoading={isLoading}
                  disabledBgColor='#9E9E9E'
                  onClick={() => handleCreateObjectAndAction()}
                  disabled={handleDisabledButton()}>
                  Finalizar
                </Button>
              </Box>
            </Box>
          </WrapperContent>
        </WrapperContentArea>
      </Box>
    </ContainerPageSelfRegister>
  )
}

export default CameraRtmpActions
